import { useEffect, useRef } from 'react'

/**
 * Requests a screen wake lock to keep the phone screen on while the race is active.
 * This prevents the OS from automatically locking the screen, which would suspend
 * GPS access and location polling. If the user manually locks the phone, the wake lock
 * is released — it re-acquires automatically when the app returns to the foreground.
 *
 * Note: wake locks are not supported on iOS Safari. Android Chrome supports them fully.
 */
export function useWakeLock(active: boolean) {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)

  const acquire = async () => {
    if (!active || !('wakeLock' in navigator)) return
    if (wakeLockRef.current && !wakeLockRef.current.released) return
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen')
    } catch {
      // Permission denied or not supported — fail silently
    }
  }

  const release = () => {
    if (wakeLockRef.current && !wakeLockRef.current.released) {
      wakeLockRef.current.release()
    }
    wakeLockRef.current = null
  }

  // Acquire/release when active flag changes
  useEffect(() => {
    if (active) {
      acquire()
    } else {
      release()
    }
    return release
  }, [active]) // eslint-disable-line react-hooks/exhaustive-deps

  // Wake lock is automatically released when the page is hidden.
  // Re-acquire it as soon as the page becomes visible again.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && active) acquire()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [active]) // eslint-disable-line react-hooks/exhaustive-deps
}
